import { useCallback, useEffect, useId, useMemo, useRef } from 'react';
import type { KeyboardEvent } from 'react';

interface Props {
  value: string;
  onChange: (next: string) => void;
  /** Sichtbares Label (oder sr-only). */
  label?: string;
  hideLabel?: boolean;
  ariaLabel?: string;
  /** Editor-Höhe als Tailwind-Klasse. */
  heightClass?: string;
  /** Disabled-Zustand. */
  disabled?: boolean;
  /** Zeigt links eine Zeilen-Spalte synchron zum Scroll. */
  showLineNumbers?: boolean;
  className?: string;
  placeholder?: string;
}

/**
 * YamlEditor - Textarea mit YAML-spezifischen Komfort-Features.
 *
 * - Tab fügt 2 Spaces ein (kein Focus-Wechsel)
 * - Shift+Tab dedented (entfernt 2 Spaces am Zeilenanfang)
 * - Enter behält die Indentierung der vorherigen Zeile
 * - Live Line-Number-Spalte links, scroll-synchronisiert
 *
 * Bewusst KEIN Monaco/CodeMirror: bringt ~1MB Bundle. Für Sprint 6 reicht
 * eine Textarea + 50 LOC Logik. Wenn der Editor-Komfort später zu dünn
 * wirkt, kann man hier upgraden ohne Konsumenten zu brechen.
 */
export function YamlEditor({
  value,
  onChange,
  label,
  hideLabel = false,
  ariaLabel,
  heightClass = 'h-[400px]',
  disabled = false,
  showLineNumbers = true,
  className,
  placeholder,
}: Props): JSX.Element {
  const id = useId();
  const ref = useRef<HTMLTextAreaElement>(null);
  const gutterRef = useRef<HTMLDivElement>(null);

  const lineCount = useMemo(() => value.split('\n').length, [value]);

  const onKey = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>): void => {
      const ta = event.currentTarget;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;

      if (event.key === 'Tab') {
        event.preventDefault();
        if (event.shiftKey) {
          // Dedent: entfernt 2 führende Spaces an Cursor-Zeile.
          const lineStart = ta.value.lastIndexOf('\n', start - 1) + 1;
          const head = ta.value.slice(0, lineStart);
          const tail = ta.value.slice(lineStart);
          const dedented = tail.replace(/^ {1,2}/, () => '');
          const removed = tail.length - dedented.length;
          const next = head + dedented;
          onChange(next);
          requestAnimationFrame(() => {
            ta.selectionStart = ta.selectionEnd = Math.max(lineStart, start - removed);
          });
        } else {
          // Indent: 2 Spaces einfügen.
          const next = ta.value.slice(0, start) + '  ' + ta.value.slice(end);
          onChange(next);
          requestAnimationFrame(() => {
            ta.selectionStart = ta.selectionEnd = start + 2;
          });
        }
        return;
      }

      if (event.key === 'Enter') {
        // Indent-Persistenz: kopiere führende Spaces der aktuellen Zeile.
        const lineStart = ta.value.lastIndexOf('\n', start - 1) + 1;
        const currentLine = ta.value.slice(lineStart, start);
        const lead = currentLine.match(/^[ ]*/)?.[0] ?? '';
        // Wenn Zeile auf ":" endet -> 2 Spaces extra (neuer Block).
        const extra = /:\s*$/.test(currentLine) ? '  ' : '';
        if (lead.length === 0 && extra.length === 0) return; // Default-Verhalten
        event.preventDefault();
        const insert = '\n' + lead + extra;
        const next = ta.value.slice(0, start) + insert + ta.value.slice(end);
        onChange(next);
        requestAnimationFrame(() => {
          ta.selectionStart = ta.selectionEnd = start + insert.length;
        });
      }
    },
    [onChange]
  );

  // Scroll-Sync: Gutter folgt der Textarea.
  useEffect(() => {
    const ta = ref.current;
    const gutter = gutterRef.current;
    if (!ta || !gutter) return;
    const onScroll = (): void => {
      gutter.scrollTop = ta.scrollTop;
    };
    ta.addEventListener('scroll', onScroll);
    return () => ta.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className={['flex flex-col gap-1', className ?? ''].join(' ')}>
      {label && (
        <label
          htmlFor={id}
          className={['text-sm font-medium', hideLabel ? 'sr-only' : ''].join(' ')}
        >
          {label}
        </label>
      )}
      <div
        className={[
          'flex overflow-hidden rounded-md border border-border bg-[#0b0d12]',
          heightClass,
        ].join(' ')}
      >
        {showLineNumbers && (
          <div
            ref={gutterRef}
            aria-hidden="true"
            className="select-none overflow-hidden border-r border-zinc-800 bg-[#0b0d12] px-2 py-2 text-right font-mono text-xs leading-relaxed text-zinc-500"
            style={{ width: `${Math.max(2, String(lineCount).length) + 1}ch` }}
          >
            {Array.from({ length: lineCount }, (_, i) => (
              <div key={i}>{i + 1}</div>
            ))}
          </div>
        )}
        <textarea
          id={id}
          ref={ref}
          value={value}
          spellCheck={false}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          disabled={disabled}
          aria-label={!label && ariaLabel ? ariaLabel : undefined}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKey}
          className="flex-1 resize-none bg-transparent p-2 font-mono text-xs leading-relaxed text-zinc-100 placeholder:text-zinc-600 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="yaml-editor"
        />
      </div>
    </div>
  );
}
